import type { AppData, Period } from "@/types";

// ---- Puntos de Volumen (PV) acumulados por entradas de stock ----
// Regla: los PV suman SOLO cuando entra mercancía al stock propio (cada compra
// que registra el admin, con el PV configurado en el producto). Las ventas NO
// vuelven a sumar PV porque esa mercancía ya sumó al entrar. Los puntos de
// referidos son un valor manual que también suma a la calificación y se
// REINICIA cada mes calendario (cada mes arranca de cero).

// Clave de mes en formato "yyyy-mm".
export const monthKeyOf = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

export const currentMonthKey = (): string => monthKeyOf(new Date());

// Suma de puntos de referidos del mapa mensual para un período (mes o año).
export const referralPvForPeriod = (
  map: Record<string, number> | undefined,
  period: Period
): number => {
  if (!map) return 0;
  if (period.month === "anual") {
    const prefix = `${period.year}-`;
    return Object.entries(map)
      .filter(([k]) => k.startsWith(prefix))
      .reduce((acc, [, v]) => acc + Math.max(0, Number(v) || 0), 0);
  }
  const key = `${period.year}-${String(period.month + 1).padStart(2, "0")}`;
  return Math.max(0, Number(map[key]) || 0);
};

export interface PeriodPv {
  stockPv: number; // PV de las compras de stock dentro del período
  referralPv: number; // puntos de referidos del período (manual, por mes)
  qualifyingPv: number; // stockPv + referralPv: lo que califica Amway
  units: number; // unidades ingresadas al stock en el período
  entries: number; // número de compras registradas en el período
}

// PV que "califica" Amway para un período: las compras de stock de ese mes
// (o del año completo si el período es anual) más los puntos de referidos.
// Al cambiar de mes arranca de cero, sin arrastrar histórico.
export const pvForPeriod = (data: AppData, period: Period): PeriodPv => {
  const entries = (data.stockEntries ?? []).filter((e) => {
    const d = new Date(e.date);
    if (Number.isNaN(d.getTime())) return false;
    if (d.getFullYear() !== period.year) return false;
    if (period.month === "anual") return true;
    return d.getMonth() === period.month;
  });
  const stockPv = entries.reduce((a, e) => a + e.pvTotal, 0);
  const units = entries.reduce((a, e) => a + e.qty, 0);
  const referralPv = referralPvForPeriod(data.settings.referralPvByMonth, period);
  return {
    stockPv,
    referralPv,
    qualifyingPv: stockPv + referralPv,
    units,
    entries: entries.length,
  };
};

export interface MonthlyPv {
  key: string; // yyyy-mm
  label: string; // etiqueta corta, ej. "may 2026"
  pv: number; // PV de compras (entradas a stock) del mes
  referralPv: number; // puntos de referidos ingresados ese mes
  totalPv: number; // pv + referralPv
  units: number; // unidades ingresadas al stock
  entries: number; // número de compras registradas
}

export interface PvSummary {
  months: MonthlyPv[];
  totalStockPv: number; // PV de compras (entradas a stock) de todos los meses
  stockUnits: number; // unidades totales ingresadas
  totalReferralPv: number; // referidos de todos los meses
  globalPv: number; // totalStockPv + totalReferralPv
  currentMonthKey: string; // yyyy-mm del mes actual
  currentMonthLabel: string; // etiqueta del mes actual
  currentStockPv: number; // PV por compras del mes actual
  currentReferralPv: number; // referidos del mes actual
  currentQualifyingPv: number; // PV que califica este mes
}

const SHORT_MONTHS = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];

const toMonthKey = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return monthKeyOf(d);
};

const monthLabelFromKey = (key: string): string => {
  const [year, month] = key.split("-");
  const idx = Number(month) - 1;
  return `${SHORT_MONTHS[idx] ?? month} ${year}`;
};

export const buildPvSummary = (data: AppData): PvSummary => {
  const map = new Map<string, MonthlyPv>();

  const ensure = (key: string): MonthlyPv => {
    let row = map.get(key);
    if (!row) {
      row = {
        key,
        label: monthLabelFromKey(key),
        pv: 0,
        referralPv: 0,
        totalPv: 0,
        units: 0,
        entries: 0,
      };
      map.set(key, row);
    }
    return row;
  };

  let stockUnits = 0;
  (data.stockEntries ?? []).forEach((e) => {
    const key = toMonthKey(e.date);
    if (!key) return;
    stockUnits += e.qty;
    const row = ensure(key);
    row.pv += e.pvTotal;
    row.units += e.qty;
    row.entries += 1;
  });

  // Los referidos entran al mes al que pertenecen; cada mes es independiente.
  Object.entries(data.settings.referralPvByMonth ?? {}).forEach(([key, value]) => {
    const pv = Math.max(0, Number(value) || 0);
    if (pv <= 0) return;
    ensure(key).referralPv += pv;
  });

  const months = Array.from(map.values())
    .map((m) => ({ ...m, totalPv: m.pv + m.referralPv }))
    .sort((a, b) => a.key.localeCompare(b.key));

  const totalStockPv = months.reduce((a, m) => a + m.pv, 0);
  const totalReferralPv = months.reduce((a, m) => a + m.referralPv, 0);

  const curKey = currentMonthKey();
  const cur = map.get(curKey);
  const currentStockPv = cur?.pv ?? 0;
  const currentReferralPv = cur?.referralPv ?? 0;

  return {
    months,
    totalStockPv,
    stockUnits,
    totalReferralPv,
    globalPv: totalStockPv + totalReferralPv,
    currentMonthKey: curKey,
    currentMonthLabel: monthLabelFromKey(curKey),
    currentStockPv,
    currentReferralPv,
    currentQualifyingPv: currentStockPv + currentReferralPv,
  };
};